import { TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { StrategistTransition } from '@/entities/strategist';
import { cn } from '@/shared/lib';
import { RankingRow } from './RankingRow';

type Props = {
  /** Направление рейтинга: повышающие целевой исход или понижающие */
  variant: 'up' | 'down';
  transitions: StrategistTransition[];
  /** Нажатие на понятие — открыть его карточку */
  onConceptClick: (id: string) => void;
  /** Нажатие на строку — провалиться в диалоги перехода */
  onTransitionClick: (transition: StrategistTransition) => void;
};

/**
 * Таблица переходов во всю ширину: возражение → аргумент, n, targetRate %, lift (± п.п.).
 * Направление влияния (иконка) выводится в заголовке колонки lift.
 */
export const RankingTable: FC<Props> = ({ variant, transitions, onConceptClick, onTransitionClick }) => {
  const { t } = useTranslation('strategist');
  const LiftIcon = variant === 'up' ? TrendingUpIcon : TrendingDownIcon;
  const liftIconClass = variant === 'up' ? 'text-primary' : 'text-destructive';

  if (transitions.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">{t('transitions.ranking.empty')}</p>;
  }

  return (
    <table className="w-full border-collapse">
      <thead>
        <tr className="border-b border-border text-xs text-muted-foreground">
          <th className="py-2 pr-2 text-left font-normal">{t('transitions.ranking.transition')}</th>
          <th className="py-2 px-2 text-right font-normal">{t('transitions.ranking.n')}</th>
          <th className="py-2 px-2 text-right font-normal">{t('transitions.ranking.targetRate')}</th>
          <th className="py-2 pl-2 text-right font-normal">
            <span className="inline-flex items-center justify-end gap-1">
              <LiftIcon className={cn('size-3.5', liftIconClass)} />
              {t('transitions.ranking.lift')}
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        {transitions.map((transition, index) => (
          <RankingRow
            key={`${transition.fromConceptId}__${transition.toConceptId}__${index}`}
            transition={transition}
            onConceptClick={onConceptClick}
            onTransitionClick={onTransitionClick}
          />
        ))}
      </tbody>
    </table>
  );
};
